<template>
    <transition name="skeleton-fade" @after-leave="$emit('after-leave')">
        <span
            v-if="visible"
            class="skeleton"
            :class="skeletonClasses"
            :style="skeletonStyles"
            aria-hidden="true"
            v-bind="$attrs"
        />
    </transition>
</template>

<script>
const SHAPES = ['rectangle', 'rounded', 'square', 'circle', 'pill']
const ANIMATIONS = ['wave', 'pulse', 'none']

export default {
    name: 'Skeleton',
    inheritAttrs: false,
    props: {
        visible: {
            type: Boolean,
            default: true
        },
        fadeDuration: {
            type: Number,
            default: 300
        },
        shape: {
            type: String,
            default: 'rectangle',
            validator: (value) => SHAPES.includes(value)
        },
        width: {
            type: String,
            default: null
        },
        height: {
            type: String,
            default: null
        },
        size: {
            type: String,
            default: null
        },
        borderRadius: {
            type: String,
            default: null
        },
        animation: {
            type: String,
            default: 'wave',
            validator: (value) => ANIMATIONS.includes(value)
        },
        customClass: {
            type: [String, Array, Object],
            default: null
        }
    },
    computed: {
        skeletonClasses() {
            return [
                `skeleton--${this.shape}`,
                `skeleton--${this.animation}`,
                this.customClass
            ]
        },
        skeletonStyles() {
            const styles = {
                '--skeleton-fade-duration': `${this.fadeDuration}ms`
            }

            if (this.size) {
                styles.width = this.size
                styles.height = this.size
            } else {
                if (this.width) {
                    styles.width = this.width
                }

                if (this.height) {
                    styles.height = this.height
                }
            }

            if (this.borderRadius) {
                styles.borderRadius = this.borderRadius
            }

            return styles
        }
    }
}
</script>

<style lang="scss" scoped>
.skeleton {
    display: inline-block;
    vertical-align: middle;
    width: 100%;
    height: rem(14px);
    background-color: $main-t90;
    border-radius: $default-border-radius;
    overflow: hidden;
    position: relative;
    flex-shrink: 0;

    &--circle {
        border-radius: 50%;
    }

    &--rounded {
        border-radius: 8px;
    }

    &--pill {
        border-radius: rem(999px);
    }

    &--square {
        width: rem(40px);
        height: rem(40px);
        border-radius: rem(4px);
    }

    &--wave {
        &::after {
            content: '';
            position: absolute;
            inset: -50%;
            transform: translate(-100%, -100%);
            background: linear-gradient(
                -45deg,
                transparent 40%,
                rgba($neutro-light, 0.55) 50%,
                transparent 60%
            );
            animation: skeleton-wave 2.8s linear infinite;
        }
    }

    &--pulse {
        animation: skeleton-pulse 2s ease-in-out infinite;
    }

    &--none {
        &::after {
            display: none;
        }
    }
}

.skeleton-fade {
    &-leave-active {
        transition: opacity var(--skeleton-fade-duration, 300ms) ease;
    }

    &-leave-to {
        opacity: 0;
    }
}

@keyframes skeleton-wave {
    100% {
        transform: translate(100%, 100%);
    }
}

@keyframes skeleton-pulse {
    0%,
    100% {
        opacity: 1;
    }

    50% {
        opacity: 0.55;
    }
}
</style>
